/* ==========================================================================
   KITSTERS - MAGIC HOUR PRODUCT PAGE (kmhp)
   --------------------------------------------------------------------------
   Drives sections/kitsters-magic-hour-product.liquid:
     1. gallery thumb -> main image
     2. quantity stepper - PACKS, min 1, steps of 1, HARD CAP at the quote
        threshold (which is stated in kits, so the cap in packs is derived)
     3. "Order now" -> billing & delivery panel -> Magic Checkout
     4. bulk-order dialog (the cap, the 50+ hook, the one-address note)
     5. sticky order bar (every width)
     6. the four behaviours the reused retail-PDP markup needs, ported
        value-for-value from kitsters-product.js (accordions, pairs rail,
        review row, scroll-to anchors)

   Every throttle flag in here is component-prefixed on purpose. Two `var`
   flags sharing a name inside one init() are ONE variable, and the second
   scroll handler then bails forever with nothing to show for it - no error,
   no warning (CLAUDE.md §10.59).
   ========================================================================== */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-kmhp]');
    if (!root) return;

    var variantId = parseInt(root.getAttribute('data-variant-id'), 10) || 0;
    var unit = parseInt(root.getAttribute('data-unit'), 10) || 0;
    /* ── PACKS, not kits (owner 2026-08-20) ────────────────────────────────
       "Instead of single we are selling pack of 5. […] The quantity counter now
       should be start from 1 and increase +1."

       The Shopify unit IS a pack now (every Magic Hour variant was repriced
       589 -> 2945 in Admin the same day), so `qty` below is a pack count and is
       handed to /cart/add.js unchanged. `PACK` is the ONE conversion in this
       file: multiply by it wherever a number has to be printed in kits, and
       nowhere else. Two counters for one quantity is how they end up
       disagreeing. */
    var PACK = parseInt(root.getAttribute('data-pack'), 10) || 5;
    var minPacks = parseInt(root.getAttribute('data-min'), 10) || 1;
    /* The cap, not a "soft threshold". Owner 2026-08-17: the stepper stops
       here and pressing + again opens the bulk dialog. `data-quote-at` is in
       KITS - it is the number the copy, the dialog and the WhatsApp message all
       quote - and `data-max-packs` is Liquid's own division of it, so the two
       cannot drift apart the way a second stored number would. */
    var maxKits = parseInt(root.getAttribute('data-quote-at'), 10) || 50;
    var maxPacks = parseInt(root.getAttribute('data-max-packs'), 10) || Math.floor(maxKits / PACK) || 1;
    var hourTitle = root.getAttribute('data-title') || 'Magic Hour';
    /* Derived from `shop.url` (verified to render "https://kitsters.in"), not
       from `shop.domain`, and parsed rather than string-matched so a trailing
       slash or a protocol change cannot silently disable the guard. */
    var primaryHost = '';
    try { primaryHost = new URL(root.getAttribute('data-primary-origin')).host; }
    catch (e) { primaryHost = ''; }
    var waNum = root.getAttribute('data-wa') || '';
    var STEP = 1;

    /* The space after the symbol is not decoration: the shop's money_format is
       "₹ {{amount_no_decimals}}", so every server-rendered figure on this page
       carries it. Without the space, the totals panel visibly shifted by one
       character the first time the stepper was touched. */
    function inr(paise) {
      return '₹ ' + Math.round(paise / 100).toLocaleString('en-IN');
    }

    /* ── GST, taken back out of a tax-inclusive price ──────────────────────
       Owner 2026-08-17: "Please exclude the gst. Eg. 499 + 18% gst. In total we
       should show total gst."

       This MUST stay identical to the Liquid in kitsters-magic-hour-product.liquid
       (search `order_ex`), or the panel changes its numbers the first time the
       stepper moves. Liquid's `divided_by` on two integers floors, so the same
       flooring is written explicitly here rather than left to Math.round:

         ex  = floor of (total ÷ 1.18), then floored to whole rupees
         gst = total − ex          ← the REMAINDER, never 18% of ex

       Deriving GST as the remainder is what makes the three printed lines add up
       to the real charge at every quantity. The grand total is always qty × the
       stored tax-inclusive price - the amount Shopify will actually take. */
    var GST_RATE = 18;
    function exGst(totalPaise) {
      var raw = Math.floor((totalPaise * 100) / (100 + GST_RATE));
      return Math.floor((raw + 50) / 100) * 100;
    }

    /* ── 1. Gallery ────────────────────────────────────────────────────
       DEFERRED SLIDES. Every slide is `position: absolute; inset: 0` in one
       box, so they are all inside the viewport and `loading="lazy"` never
       defers any of them - measured, all eight fetched at 1100w on first
       paint, 666 KB across 7 requests on a 390px phone. So the section ships
       slides 2..n with `data-src`/`data-srcset` and no `src`, and they are
       promoted here.

       Promotion happens on pointerenter and focus as well as click, so the
       fetch normally starts while the finger or cursor is still travelling
       and the swap is done before the click lands. Promoting is idempotent -
       the attributes are removed as they are consumed. */
    var slides = root.querySelectorAll('[data-gallery-slide]');
    var thumbs = root.querySelectorAll('[data-gallery-thumb]');

    function promoteSlide(idx) {
      var slide = root.querySelector('[data-gallery-slide="' + idx + '"]');
      if (!slide) return;
      slide.querySelectorAll('img[data-src]').forEach(function (im) {
        var ss = im.getAttribute('data-srcset');
        // srcset BEFORE src: with both present the browser picks from srcset,
        // and setting src first can start a second, wasted request for it.
        if (ss) { im.srcset = ss; im.removeAttribute('data-srcset'); }
        im.src = im.getAttribute('data-src');
        im.removeAttribute('data-src');
      });
    }

    /* ONE place that changes the photo, driven by the thumbs and by the arrows
       (owner 2026-08-20). Two copies of this - one per control - is how a
       gallery ends up with a thumb row and an arrow pair that disagree about
       which slide is showing, and the arrows would have had no way to keep
       `aria-pressed` on the thumbs honest. */
    /* Below 768px the thumb row is a horizontal rail (owner 2026-08-20), so the
       thumb that just became active can be off screen - which makes the arrows
       look like they changed the photo and nothing else, with no way to tell
       where you are in eight photos. This centres it in the rail.

       `scrollLeft` math on the ROW, never `scrollIntoView`: that method scrolls
       every scrollable ancestor, so on a phone where the gallery is only
       part-visible it would yank the PAGE as well - a scroll the shopper did
       not ask for, in the middle of tapping an arrow (§10.36's neighbour).

       Position is computed from `getBoundingClientRect` deltas plus the row's
       current `scrollLeft`, not from `offsetLeft`: offsetLeft is measured
       against the offsetParent, which is not this row, and it does not move
       with the row's own scrolling - so the two disagree the moment the rail
       has been scrolled at all.

       Bails when the row is not actually scrollable, so it is inert on desktop
       (where the row is an 8-column grid) with no breakpoint to keep in step. */
    var thumbRow = root.querySelector('.kmhp__thumbs');

    function revealThumb(idx) {
      if (!thumbRow) return;
      if (thumbRow.scrollWidth <= thumbRow.clientWidth + 1) return;
      var t = thumbRow.querySelector('[data-gallery-thumb="' + idx + '"]');
      if (!t) return;
      var rowBox = thumbRow.getBoundingClientRect();
      var tBox = t.getBoundingClientRect();
      var target = thumbRow.scrollLeft + (tBox.left - rowBox.left) -
        (thumbRow.clientWidth - tBox.width) / 2;
      target = Math.max(0, Math.min(target, thumbRow.scrollWidth - thumbRow.clientWidth));
      var reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (thumbRow.scrollTo) {
        thumbRow.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
      } else {
        thumbRow.scrollLeft = target;
      }
    }

    function showSlide(idx) {
      idx = String(idx);
      promoteSlide(idx);
      slides.forEach(function (s) {
        s.classList.toggle('is-on', s.getAttribute('data-gallery-slide') === idx);
      });
      thumbs.forEach(function (t) {
        var on = t.getAttribute('data-gallery-thumb') === idx;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      revealThumb(idx);
    }

    /* The index currently on screen, read from the DOM rather than tracked in a
       variable: the lightbox and a future control can both change the active
       slide, and a counter that only this block writes would go stale without
       anything erroring (§10.51 - assert on what is rendered, not on what you
       set). */
    function activeIndex() {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].classList.contains('is-on')) {
          return parseInt(slides[i].getAttribute('data-gallery-slide'), 10) || 0;
        }
      }
      return 0;
    }

    if (slides.length > 1 && thumbs.length) {
      thumbs.forEach(function (thumb) {
        var idx = thumb.getAttribute('data-gallery-thumb');
        ['pointerenter', 'focus'].forEach(function (evt) {
          thumb.addEventListener(evt, function () { promoteSlide(idx); });
        });
        thumb.addEventListener('click', function () { showSlide(idx); });
      });
    }

    /* ── Prev / next arrows on the frame (owner 2026-08-20) ───────────────
       They WRAP rather than disable at the ends: a two-photo gallery with a
       disabled arrow reads as broken, and a disabled control cannot be focused
       or announced (the same reasoning as the quantity stepper's + button).

       `stopPropagation` because the frame carries a click-anywhere zoom
       handler. assets/kitsters-lightbox.js already bails on `.kp-gallery__btn`
       - checked, it is first in its bail list - so this is a belt; it stays
       because the belt is one line and the alternative is the overlay opening
       on every arrow press the day that list is edited (§10.23).

       The NEXT slide is promoted on hover/focus as well, so its bytes are
       usually already in flight before the click lands - the same trick the
       thumbs use (§10.90). */
    if (slides.length > 1) {
      var arrowStep = function (delta) {
        var next = (activeIndex() + delta + slides.length) % slides.length;
        showSlide(next);
      };
      var bindArrow = function (sel, delta) {
        var btn = root.querySelector(sel);
        if (!btn) return;
        ['pointerenter', 'focus'].forEach(function (evt) {
          btn.addEventListener(evt, function () {
            promoteSlide((activeIndex() + delta + slides.length) % slides.length);
          });
        });
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          arrowStep(delta);
        });
      };
      bindArrow('[data-gallery-prev]', -1);
      bindArrow('[data-gallery-next]', 1);
    }

    /* The lightbox (assets/kitsters-lightbox.js) zooms whatever slide is
       active, and the click-anywhere branch can fire on a slide whose thumb
       was never touched - a keyboard user who tabbed to the gallery, say. Any
       still-deferred slide is promoted before it can be zoomed, in the capture
       phase so it runs before the lightbox's own document listener. */
    var galleryMain = root.querySelector('.kmhp__gallery-main');
    if (galleryMain) {
      galleryMain.addEventListener('click', function () {
        var on = galleryMain.querySelector('[data-gallery-slide].is-on');
        if (on) promoteSlide(on.getAttribute('data-gallery-slide'));
      }, true);
    }

    /* ── 2. Bulk-order dialog ────────────────────────────────────────────
       Declared before the stepper because the cap opens it. A plain <dialog>
       that no component owns, so showModal()/close() are the correct API -
       §10.70's "never call showModal() directly" is specifically about Kalles
       drawers, whose DrawerComponent builds its slide transform inside the
       `show` getter and is poisoned by a raw open. The close is still made
       idempotent, because a double-close IS a real interaction here: the
       dialog carries two [data-bulk-close] buttons and Esc as well. */
    var bulkModal = root.querySelector('[data-bulk]');
    var bulkWa = root.querySelector('[data-bulk-wa]');

    /* Takes PACKS and talks in KITS: the shopper's mental model at this point
       is headcount ("about 55 people"), which is kits, and the dialog's copy
       and the 50+ hook beside it both quote the kit figure. */
    function bulkMessage(packsWanted) {
      var wanted = (packsWanted || 0) * PACK;
      var n = wanted > maxKits ? wanted : maxKits + PACK;
      return 'Hi! We are planning a Magic Hour for about ' + n + ' people (' +
        hourTitle + '). Can you send a quote?';
    }

    function openBulk(qtyWanted) {
      if (!bulkModal) return;
      /* Rebuild the WhatsApp link from the number on the section root, never
         by re-parsing the href we last wrote - that round-trip breaks the
         moment the link carries anything else. The Liquid-rendered href stays
         the fallback when the number is missing. */
      if (bulkWa && waNum) {
        bulkWa.setAttribute('href',
          'https://wa.me/' + waNum + '?text=' + encodeURIComponent(bulkMessage(qtyWanted)));
      }
      if (typeof bulkModal.showModal === 'function') {
        if (!bulkModal.open) bulkModal.showModal();
      } else {
        /* No <dialog> support (old Safari): the link still works, so send the
           shopper straight to the conversation rather than doing nothing. */
        if (bulkWa) window.open(bulkWa.getAttribute('href'), '_blank', 'noopener');
      }
    }

    function closeBulk() {
      if (!bulkModal || !bulkModal.open) return;
      bulkModal.close();
    }

    root.querySelectorAll('[data-bulk-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { openBulk(0); });
    });
    root.querySelectorAll('[data-bulk-close]').forEach(function (btn) {
      btn.addEventListener('click', closeBulk);
    });
    if (bulkModal) {
      /* Click on the backdrop. The dialog's own rect is the only reliable test:
         a click on ::backdrop reports the <dialog> itself as the target, so
         comparing coordinates is what distinguishes it from a click on the
         card inside. */
      bulkModal.addEventListener('click', function (e) {
        if (e.target !== bulkModal) return;
        var r = bulkModal.getBoundingClientRect();
        var inside = e.clientX >= r.left && e.clientX <= r.right &&
                     e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) closeBulk();
      });
    }

    /* ── 3. Quantity + totals ────────────────────────────────────────── */
    var qtyInput = root.querySelector('[data-qty-input]');
    var decBtn = root.querySelector('[data-qty-dec]');
    var incBtn = root.querySelector('[data-qty-inc]');
    var totalLabel = root.querySelector('[data-total-label]');
    var totalSub = root.querySelector('[data-total-sub]');
    var totalGst = root.querySelector('[data-total-gst]');
    var totalGrand = root.querySelector('[data-total-grand]');
    var qtyKits = root.querySelector('[data-qty-kits]');
    var qty = minPacks;

    /* Snap to the nearest legal PACK count: at least the minimum, at most the
       cap, on the step grid measured FROM the minimum. Typing 999 in the field
       lands on the cap rather than being silently accepted - the field and the
       buttons must agree about what is orderable. */
    function normalise(n) {
      if (!isFinite(n)) return minPacks;
      if (n <= minPacks) return minPacks;
      var steps = Math.round((n - minPacks) / STEP);
      var snapped = minPacks + steps * STEP;
      return snapped > maxPacks ? maxPacks : snapped;
    }

    function render() {
      if (qtyInput) qtyInput.value = qty;
      if (decBtn) decBtn.disabled = qty <= minPacks;
      /* The + button is deliberately NOT disabled at the cap. A disabled
         control cannot be clicked, focused or announced, so the shopper who
         wants 80 kits would get silence - the whole point of the instruction
         is that pressing + at 50 SAYS something.

         And it must not carry `aria-disabled` either. That attribute announces
         the control as inert, which would be a LIE: at the cap the button is
         the most useful thing on the page, because it opens the bulk-order
         conversation. Announcing "disabled" while still acting on the press is
         the §10.28 broken-promise shape pointed at assistive tech instead of at
         the shopper. (It also makes the control untestable - Playwright's
         actionability check treats aria-disabled="true" as not-enabled and
         refuses to click, which is how this was caught.)

         What changes at the cap is the accessible NAME, so a screen-reader user
         is told what the press will actually do. */
      if (incBtn) {
        incBtn.setAttribute(
          'aria-label',
          qty >= maxPacks
            ? 'Order more than ' + maxKits + ' kits - opens a bulk-order enquiry'
            : 'More packs'
        );
      }

      /* The live kit count beside the field. It is the number the buyer is
         actually deciding ("is this enough people?"), so it is rendered rather
         than left to be worked out - and it is derived here from the same `qty`
         the totals use, never stored, so it cannot fall out of step. */
      if (qtyKits) qtyKits.textContent = '= ' + (qty * PACK) + ' kits';

      /* The label deliberately does NOT read "qty × unit price" any more. The
         unit price is displayed rounded to ₹499 while the exact ex-GST figure
         is ₹499.15, so printing the multiplication would invite an arithmetic
         check that fails by a couple of rupees at larger quantities. It states
         the quantity and what the figure beside it excludes; the multiplication
         nobody can dispute - qty × the real charge - is the grand total. */
      var total = qty * unit;
      var ex = exGst(total);
      if (totalLabel) {
        totalLabel.textContent =
          qty + ' \u00d7 pack of ' + PACK + ' \u00b7 ' + (qty * PACK) + ' kits (excl. GST)';
      }
      if (totalSub) totalSub.textContent = inr(ex);
      if (totalGst) totalGst.textContent = inr(total - ex);
      if (totalGrand) totalGrand.textContent = inr(total);
    }

    if (incBtn) {
      incBtn.addEventListener('click', function () {
        if (qty >= maxPacks) { openBulk(qty + STEP); return; }
        qty = normalise(qty + STEP);
        render();
      });
    }
    if (decBtn) decBtn.addEventListener('click', function () { qty = normalise(qty - STEP); render(); });
    if (qtyInput) {
      ['change', 'blur'].forEach(function (evt) {
        qtyInput.addEventListener(evt, function () {
          var typed = parseInt(qtyInput.value, 10);
          qty = normalise(typed);
          render();
          if (isFinite(typed) && typed > maxPacks) openBulk(typed);
        });
      });
    }
    render();

    /* ── 4. "Order now" -> billing & delivery panel ───────────────────── */
    var detailsForm = root.querySelector('[data-details]');
    var openBtn = root.querySelector('[data-order-open]');
    var errorEl = root.querySelector('[data-details-error]');

    function openDetails(focusIt) {
      if (!detailsForm) return;
      detailsForm.hidden = false;
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      if (focusIt) {
        var first = detailsForm.querySelector('input, textarea');
        /* focus() cancels an in-flight smooth scroll and re-scrolls from
           wherever the page currently is (§10.36), so scroll first and focus
           with preventScroll. Wrapped because old Safari ignores the options
           object and scrolls anyway. */
        detailsForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        if (first) {
          setTimeout(function () {
            try { first.focus({ preventScroll: true }); } catch (e) { /* old Safari */ }
          }, 400);
        }
      }
    }

    if (openBtn) {
      openBtn.addEventListener('click', function () {
        if (detailsForm && !detailsForm.hidden) {
          /* Already open - treat a second press as "go on then" rather than as
             a collapse, so the button never feels like it undid the last one. */
          var submitBtn = detailsForm.querySelector('[data-buy-now]');
          if (submitBtn) submitBtn.click();
          return;
        }
        openDetails(true);
      });
    }

    /* The delivery-address field, its "same as registered" checkbox and their
       syncAddress() toggle were REMOVED 2026-08-18 (owner) - Magic Checkout
       collects the shipping address in its own overlay, so the panel's copy
       was a duplicate. The registered (GST) address stays: the overlay does
       not collect that. */

    /* ── 5. Checkout hand-off ────────────────────────────────────────────
       Clear the cart, write the panel's fields as CART ATTRIBUTES + NOTE, add
       qty x variant with the same fields as LINE ITEM PROPERTIES, then
       location.assign() a CHECKOUT URL that carries the billing address as
       `?checkout[…]=` parameters.

       ⚠️ REWRITTEN 2026-08-19, AND THE COMMENT IT REPLACED SAID THE OPPOSITE.
       The old version clicked a hidden name="checkout" submit so Razorpay's
       delegated listener would intercept it, and carried a long note explaining
       that Magic Checkout's prefill object holds only `email`, `contact` and
       `coupon_code` so the panel's values "cannot be typed into the checkout's
       fields by any theme code". Every word of that is still true OF MAGIC
       CHECKOUT - and irrelevant here, because Magic Checkout is not in this
       page's path:

         The 27 Magic Hour kits are published to "Online Store" ONLY, never to
         the "Kitsters - magic checkout" publication, so Razorpay declines them
         and falls back to Shopify's own checkout (§10.89). Verified 2026-08-19
         via `resourcePublicationsV2` on all 27, and by following a real cart
         permalink to `/checkouts/cn/…` with no Razorpay overlay in the bytes.
         Owner the same day: "Only for Magic Hour Product, we are using default
         shopify checkout."

       So the click was pure loss: Razorpay intercepted it, found the product
       ineligible and ran its own `location.href = "/checkout"`, a fresh
       navigation that drops any parameter we might have attached. Navigating
       ourselves is what makes prefill reachable at all.

       WHAT IS MEASURED, on the live store, fresh cookie jar, first visit
       (§10.79 - the control run with the parameters removed returned every one
       of these inputs EMPTY, which is the only reason the pass counts):

         checkout[email]                              -> email field
         checkout[shipping_address][first_name/last_name]
         checkout[shipping_address][address1/address2/city/zip]
         checkout[shipping_address][province]         -> zone select  (code, e.g. MH)
         checkout[shipping_address][country]          -> country select ("India" -> IN)
         checkout[shipping_address][phone]            -> phone  (Shopify prepends +91)

       …all arriving as real `value=` attributes on checkout's own inputs.

       THE ADDRESS HALF IS CONDITIONAL (2026-08-19). A "Deliver to this address
       too" tick decides whether the address block is sent at all - see the
       comment on the checkbox in the section. Identity and contact (email,
       company name, phone) go either way; the street/city/state/PIN only when
       the registered address IS the delivery address.

       🚫 THERE IS NO `checkout[billing_address][…]` PREFILL. Shopify prefills
       the SHIPPING address only, and its billing radio then defaults to "same
       as shipping". Measured against the live store with a control:

         email + shipping_address            -> both applied
         email + billing_address             -> NOTHING applied (== no params)
         email + shipping + billing_address  -> email + shipping; billing ignored
         email alone                         -> NOTHING applied

       Two things fall out of that table and both are load-bearing. `email` does
       NOT apply on its own - it needs a shipping address alongside it, which is
       why the identity block above always sends a name. And `billing_address`
       is inert rather than poisonous, so sending it would look harmless and
       still be a lie; it is not sent.

       TWO THINGS THAT ARE **NOT** PROVEN AND MUST NOT BE WRITTEN AS IF THEY
       WERE (§10.72 - an assertion is only worth writing if you know what its
       failure looks like):
         · `checkout[shipping_address][company]` is accepted into the checkout
           SESSION (it comes back in the serialised session JSON) but this
           store's checkout renders NO company input - measured, zero
           `name="company"` fields - because the company address field is off in
           checkout settings. Whether it reaches the order is unverified. It is
           sent anyway (free if it lands, harmless if it does not) and the
           company name is carried by three other routes regardless.
         · Nothing here is confirmed on a real ORDER until one is placed.
           Attributes/properties/note are plain Shopify behaviour with Razorpay
           out of the path, so the risk is low - but low is not zero (docs/19
           §MH-PREFILL).

       THE NAME FIELD IS THE COMPANY. Owner: "In name, we want company name
       should be present not any person name." Shopify marks BOTH firstName and
       lastName required (measured), so the company is SPLIT at its last space -
       "Acme Innovations Pvt" + "Ltd" - which renders back as exactly the
       company name. Padding lastName with "." also validates and puts a stray
       dot on the GST invoice, so it is not done; a single-word company leaves
       lastName empty and the shopper completes one field.

       🚫 DO NOT REBUILD THE /contact EMAIL LOG. A carrier POSTing the panel's
       fields through Shopify's storefront contact form was written, shipped to
       a branch, and REMOVED 2026-08-18 unmerged. Shopify answers a headless
       POST to /contact with **403** - measured four times against a real
       `shopify theme dev` session while `/cart/*.js` POSTs from the same page
       returned 200, so it is the endpoint, not the proxy. The cause is
       server-side captcha validation, which an AJAX request carries no token
       for; the only documented workaround is disabling spam protection
       store-wide. A page-source grep for an injected `storefront-recaptcha`
       loader finds NOTHING and proves NOTHING (§10.92). If a pre-payment record
       is ever genuinely needed, use an endpoint with no captcha - a Google Apps
       Script web app - not this one. */
    var buyBtn = root.querySelector('[data-buy-now]');
    var buyLabel = root.querySelector('[data-buy-label]');
    var restLabel = buyLabel ? buyLabel.textContent : 'Continue to payment';
    var pending = false;
    var pendingStarted = 0;
    var pendingTimer = null;
    var restoreArmed = false;
    var GRACE_MS = 1500;

    function backOnPage() { return Date.now() - pendingStarted > GRACE_MS; }

    function restoreBuy() {
      if (!pending) return;
      pending = false;
      if (buyBtn) buyBtn.disabled = false;
      if (buyLabel) buyLabel.textContent = restLabel;
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }

    /* Magic Checkout opens an in-page OVERLAY, so the document never unloads
       and there is no "we came back" event. Every one of these paths is a
       different way back and they are mutually exclusive (§10.71). pageshow is
       gated on `persisted` because a stray persisted=false pageshow fires
       ~1.2s into the hand-off on the preview theme. */
    function armRestore() {
      if (restoreArmed) return;
      restoreArmed = true;
      window.addEventListener('pageshow', function (e) { if (e.persisted) restoreBuy(); });
      document.addEventListener('visibilitychange', function () { if (!document.hidden) restoreBuy(); });
      window.addEventListener('focus', function () { if (backOnPage()) restoreBuy(); });
      document.addEventListener('pointerdown', function (e) { if (e.isTrusted && backOnPage()) restoreBuy(); }, true);
      document.addEventListener('keydown', function (e) { if (e.isTrusted && backOnPage()) restoreBuy(); }, true);
    }

    function fieldValue(name) {
      var el = root.querySelector('[data-field="' + name + '"]');
      return el ? el.value.trim() : '';
    }

    /* The state <select> holds Shopify's zone CODE as its value and the human
       name as its label. The order needs the name (nobody reads "MH" on an
       invoice) and the checkout URL needs the code, so both are read here and
       never re-derived from each other. */
    /* Ticked = the registered address is also the delivery address. Defaults to
       ticked, so the out-of-the-box behaviour is unchanged from what shipped
       before this. Missing element reads as ticked for the same reason. */
    function deliverHere() {
      var el = root.querySelector('[data-field-deliver-here]');
      return el ? !!el.checked : true;
    }

    function provinceCode() { return fieldValue('province'); }
    function provinceName() {
      var sel = root.querySelector('[data-field="province"]');
      if (!sel || sel.selectedIndex < 0) return '';
      var opt = sel.options[sel.selectedIndex];
      return opt && opt.value ? opt.textContent.trim() : '';
    }

    /* Digits only, then +91 if it looks like a bare Indian mobile. Shopify does
       this itself when a country is present (measured: "9820098200" came back
       as "+919820098200"), so this is belt-and-braces for the value that goes
       ON THE ORDER, where nothing normalises it for us. */
    function normalisedPhone() {
      var raw = fieldValue('phone').replace(/[^\d+]/g, '');
      if (/^[6-9]\d{9}$/.test(raw)) return '+91' + raw;
      if (/^0[6-9]\d{9}$/.test(raw)) return '+91' + raw.slice(1);
      if (/^91[6-9]\d{9}$/.test(raw)) return '+' + raw;
      return raw;
    }

    /* One address string for the humans who read the order, assembled from the
       structured fields rather than typed as a blob - see the section comment
       for why the blob had to go. */
    function addressLine() {
      return [
        fieldValue('address1'),
        fieldValue('address2'),
        fieldValue('city'),
        provinceName(),
        fieldValue('zip'),
        fieldValue('country') || 'India'
      ].filter(Boolean).join(', ');
    }

    function orderAttributes() {
      var company = fieldValue('company');
      /* GSTIN is stored uppercased regardless of how it was typed - the CSS
         text-transform is presentation only and does not change the value the
         form submits, so the normalisation has to happen here or half the
         orders arrive in lower case. */
      var gstin = fieldValue('gstin').toUpperCase();

      return {
        'Company name': company,
        'GST number': gstin,
        'Ordered by': fieldValue('contact_name'),
        'Designation': fieldValue('designation'),
        'Contact email': fieldValue('email'),
        'Contact phone': normalisedPhone(),
        'Registered address': addressLine(),
        /* Fulfilment reads this: with "No", the parcel goes to whatever the
           shopper typed at checkout and the address above is the invoice
           address only. Without it the two are indistinguishable on the order. */
        'Deliver to registered address': deliverHere() ? 'Yes' : 'No',
        'Order type': 'Magic Hour - ' + hourTitle,
        /* In KITS, because that is what fulfilment picks and what the invoice
           line has to read. `qty` is packs. */
        'Kits': String(qty * PACK),
        /* A computed key would need ES2015 syntax this file does not use, so
           the pack size lives in the VALUE - "2 x pack of 5" - which reads
           correctly on the order either way. */
        'Packs': String(qty) + ' \u00d7 pack of ' + PACK
      };
    }

    /* ── The checkout URL ──────────────────────────────────────────────────
       Every parameter here is one Shopify documents for cart permalinks and
       honours on `/checkout` too - re-measured on this store 2026-08-19 against
       an empty control. `add` skips blanks so an optional field never sends
       `…[address2]=`, which would otherwise blank a value the shopper's own
       browser autofill had put there. */
    function checkoutUrl() {
      var parts = [];
      function add(key, val) {
        if (!val) return;
        parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(val));
      }

      /* Company name -> the checkout's NAME field, split at the last space
         because Shopify marks first AND last name required. A single-word
         company leaves lastName empty and the shopper types one character;
         padding it with "." would validate and then print a stray dot on the
         invoice. */
      var company = fieldValue('company');
      var firstName = company;
      var lastName = '';
      var cut = company.lastIndexOf(' ');
      if (cut > 0) {
        firstName = company.slice(0, cut).trim();
        lastName = company.slice(cut + 1).trim();
      }

      /* IDENTITY + CONTACT - always sent, whichever way the tick goes. These
         are the things the owner wants gathered here precisely because the
         shopper can change everything else at checkout. */
      add('checkout[email]', fieldValue('email'));
      add('checkout[shipping_address][first_name]', firstName);
      add('checkout[shipping_address][last_name]', lastName);
      /* Accepted into the checkout session but this store renders no company
         input (the field is off in checkout settings), so treat it as a bonus
         and never as the carrier - see the §5 header. */
      add('checkout[shipping_address][company]', company);
      add('checkout[shipping_address][phone]', normalisedPhone());

      /* THE ADDRESS - only when the shopper says it is also the delivery
         address. Unticked, checkout opens with the street / city / state / PIN
         blank and the shopper fills the delivery address there, which is the
         separation the owner asked for. Measured: a payload of email + name +
         phone with no street applies all three and leaves the address fields
         empty, so this is a real half-prefill and not an all-or-nothing.

         `country` rides with the address rather than being always-on: on its
         own it pre-selects a country the shopper may be about to change, and
         it buys nothing when the rest of the address is blank. */
      if (deliverHere()) {
        add('checkout[shipping_address][address1]', fieldValue('address1'));
        add('checkout[shipping_address][address2]', fieldValue('address2'));
        add('checkout[shipping_address][city]', fieldValue('city'));
        add('checkout[shipping_address][province]', provinceCode());
        add('checkout[shipping_address][zip]', fieldValue('zip'));
        add('checkout[shipping_address][country]', fieldValue('country') || 'India');
      }

      return '/checkout' + (parts.length ? '?' + parts.join('&') : '');
    }

    /* Line item properties are the same fields minus the blanks: an empty
       "GST number: " row on the admin order line reads as data loss, where an
       absent key reads as "not provided" - and a blank GSTIN genuinely means
       the buyer chose not to claim input credit. */
    function lineProperties(attrs) {
      var out = {};
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k]) out[k] = attrs[k];
      });
      return out;
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function startCheckout() {
      if (pending) return;
      pending = true;
      pendingStarted = Date.now();
      if (buyBtn) buyBtn.disabled = true;
      if (buyLabel) buyLabel.textContent = 'Processing…';
      if (errorEl) errorEl.hidden = true;
      armRestore();
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(restoreBuy, 30000);

      var attrs = orderAttributes();
      var noteLines = [
        'Magic Hour order - ' + hourTitle,
        qty + ' \u00d7 pack of ' + PACK + ' = ' + (qty * PACK) + ' kits to ONE delivery address',
        'Company: ' + (attrs['Company name'] || '-'),
        'GSTIN: ' + (attrs['GST number'] || 'not provided'),
        'Ordered by: ' + (attrs['Ordered by'] || '-') +
          (attrs['Designation'] ? ' (' + attrs['Designation'] + ')' : ''),
        'Contact: ' + [attrs['Contact email'], attrs['Contact phone']].filter(Boolean).join(' / '),
        'Registered address: ' + (attrs['Registered address'] || '-'),
        deliverHere()
          ? 'Deliver to the registered address above.'
          : 'DIFFERENT DELIVERY ADDRESS - see the shipping address on this order. The address above is the invoice address.'
      ].join('\n');

      /* Built BEFORE the network calls, so a malformed field fails here rather
         than after the cart has already been mutated. */
      var target = checkoutUrl();

      fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('clear-failed'); })
        .then(function () {
          /* Attributes are written after the clear so no stale line precedes
             the order. NOTE: /cart/clear.js empties the LINES ONLY - it leaves
             the cart's attributes and note in place (measured 2026-09-16,
             §10.106); the comment that stood here claimed the opposite. */
          if (document.querySelector('hdt-cart-drawer')) {
            document.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
          }
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('cart:count:update', {
                detail: { item_count: cart.item_count, total_price: cart.total_price }
              }));
            })
            .catch(function () {});

          /* ⚠️ PREVIEW/DEV SURFACES SILENTLY DROP THE PREFILL - warn, do not
             pretend. Measured 2026-08-19 against the live store:

               kitsters.in/checkout?…        -> cart 5 items, EVERY field filled
               kitsters.myshopify.com/…      -> cart 0 items, every field EMPTY

             Anywhere that is not the primary domain, the hand-off crosses a
             redirect (…myshopify.com 301 -> kitsters.in 302 -> /checkouts/cn/…,
             or `shopify theme dev`'s proxy bouncing out to the real store) and
             the query string does not survive the last hop. The cart DOES
             survive a theme-dev hop, so the page looks like it worked and only
             the fields are empty - which is exactly how this was first reported.
             Navigating to an absolute primary-domain URL does not rescue it:
             the cart cookie is per-domain, so that trades empty fields for an
             empty cart (measured). There is no theme-side fix; test on the
             primary domain, where a ?preview_theme_id= preview works fine. */
          if (primaryHost && window.location.host !== primaryHost) {
            /* eslint-disable-next-line no-console */
            console.warn(
              '[kitsters] Checkout prefill will be DROPPED here. This page is on "' +
              window.location.host + '", not the storefront domain "' + primaryHost +
              '". The hand-off redirects to the primary domain and the ?checkout[…] ' +
              'parameters do not survive that hop. The cart and its line item ' +
              'properties are unaffected. To test prefill, use https://' + primaryHost +
              ' (a ?preview_theme_id= preview on that domain is fine).'
            );
          }

          /* Stamp the hand-off for kitsters-magic-cart-guard.js (sitewide):
             an abandoned checkout leaves the kits in the REAL cart, and the
             stamp is what tells the guard to remove them on the next page
             view (owner: "magic hour product never go to our cart"). The
             guard clears the stamp once the cart is clean. */
          try { window.localStorage.setItem('kt_magic_handoff', String(Date.now())); } catch (e) { /* no-op */ }

          /* A scripted navigation, never an <a href>: magic-shopify.js sweeps
             for `a[href*="/checkout"]` and would hijack a link straight back
             into Magic Checkout, which drops every parameter (§10.74a). */
          window.location.assign(target);
        })
        .catch(function () {
          restoreBuy();
          showError('We could not start checkout. Check your connection and try again, or order on WhatsApp.');
        });
    }

    if (detailsForm && variantId) {
      /* The submit event, not a click handler: it lets the browser run the
         panel's own required/pattern validation first, so an empty company
         name or a malformed GSTIN is caught before anything touches the cart. */
      detailsForm.addEventListener('submit', function (e) {
        e.preventDefault();
        startCheckout();
      });
    }

    /* ── 5b. Add to Magic Cart (owner 2026-08-19) ─────────────────────────
       Hands the current stepper quantity to the MAGIC HOUR cart -
       window.KTMagicCart, assets/kitsters-magic-cart.js, rendered by the
       footer on the two Magic Hour templates only - and opens its drawer.
       It never touches /cart/add.js: a Magic Hour kit reaches the real
       Shopify cart only inside a checkout hand-off (this page's §5, or the
       magic cart's own), never as a browsable cart line.

       The API is read at CLICK time, not at init: both scripts are deferred
       and the footer's loads after this one, so an init-time read would race
       it for nothing. If the cart script genuinely failed to load, the click
       degrades to the express path (the Order now panel) rather than dying
       silently - a button that does nothing is the §10.28 broken promise. */
    var magicAddBtn = root.querySelector('[data-magic-add]');
    var magicAddLabel = root.querySelector('[data-magic-add-label]');
    if (magicAddBtn && variantId) {
      magicAddBtn.addEventListener('click', function () {
        var api = window.KTMagicCart;
        if (!api || typeof api.add !== 'function') { openDetails(true); return; }
        api.add({
          id: variantId,
          qty: qty,
          unit: unit,
          title: hourTitle,
          url: root.getAttribute('data-url') || window.location.pathname,
          image: root.getAttribute('data-image') || '',
          min: minPacks,
          max: maxPacks
        });
        if (magicAddLabel) {
          magicAddLabel.textContent = 'Added ✓';
          setTimeout(function () { magicAddLabel.textContent = 'Add to Magic Cart'; }, 1600);
        }
        api.open();
      });
    }

    /* ── 5c. "Add to cart" on the Pairs well with rail (owner 2026-08-20) ──
       *"In 'Pairs well with' section we have to add 'add to cart' button."*

       Same destination as the buy card's own button - the MAGIC HOUR cart,
       never /cart/add.js - and the same rule about when the API is read: at
       CLICK time, because both scripts are deferred and the footer's magic-cart
       script loads after this one, so an init-time read would race it for
       nothing.

       Quantity is ONE PACK, always. The rail is a cross-sell, not a second
       buy box: a stepper per card would put two quantity controls on one screen
       disagreeing about which kit they govern, and the drawer that opens has a
       stepper on every line, so the shopper adjusts it there in the same
       gesture. Before 2026-08-20 the cards carried no button at all, because a
       unit was one kit against a five-kit minimum - so a one-tap add could not
       produce an orderable quantity. A unit IS the minimum now.

       With the cart script genuinely missing the click falls back to opening
       the kit's own page, which is where it can still be bought - a button that
       does nothing is the §10.28 broken promise.

       Bound with delegation on the rail rather than per card: the rail is
       server-rendered once, but one listener is cheaper than eight and it
       cannot miss a card that a future change renders later. */
    root.querySelectorAll('[data-kp-pairs-track]').forEach(function (track) {
      track.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-pairs-add]') : null;
        if (!btn || !track.contains(btn)) return;
        /* The card is a whole-card link (data-card-link). assets/kitsters-card-
           click.js already bails on `closest('button')` - checked, `button` is
           in its INTERACTIVE list - so this is a belt, not the mechanism. It
           stays because the belt is one line and the alternative is a silent
           navigation the day that list is edited (§10.23). */
        e.preventDefault();
        e.stopPropagation();

        var id = parseInt(btn.getAttribute('data-id'), 10) || 0;
        if (!id) return;

        var api = window.KTMagicCart;
        if (!api || typeof api.add !== 'function') {
          var href = btn.getAttribute('data-url');
          if (href) window.location.assign(href);
          return;
        }

        api.add({
          id: id,
          qty: parseInt(btn.getAttribute('data-min'), 10) || minPacks,
          unit: parseInt(btn.getAttribute('data-unit'), 10) || 0,
          title: btn.getAttribute('data-title') || 'Magic Hour',
          url: btn.getAttribute('data-url') || '',
          image: btn.getAttribute('data-image') || '',
          min: parseInt(btn.getAttribute('data-min'), 10) || minPacks,
          max: parseInt(btn.getAttribute('data-max'), 10) || maxPacks
        });

        var label = btn.querySelector('[data-pairs-add-label]');
        if (label) {
          label.textContent = 'Added \u2713';
          setTimeout(function () { label.textContent = 'Add to cart'; }, 1600);
        }
        api.open();
      });
    });

    /* ── 6. Scroll-to-buy (mobile bar + closing CTA) ─────────────────── */
    var buyCard = root.querySelector('.kmhp__buy');
    root.querySelectorAll('[data-scroll-buy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!buyCard) return;
        buyCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        openDetails(false);
      });
    });

    /* ── 7. Sticky order bar - EVERY WIDTH since 2026-08-21 ──────────────
       Owner: "can you add sticky cta bar for mh product page for desktop view
       also". The width gate that used to open this function -
       `if (!kmhpStickyMq.matches) { stickyBar.hidden = true; return; }` against
       a `(max-width: 767px)` query - is deleted, and that plus un-scoping the
       CSS is the whole change. The visibility RULE below is untouched and has
       always been width-independent, so desktop now gets exactly the behaviour
       the phone always had: the bar appears when the buy card scrolls off the
       top and retires over the closing CTA.

       Computed from getBoundingClientRect, not an IntersectionObserver: an
       element that has scrolled clean past the viewport goes from ratio 0 to
       ratio 0 without ever crossing a threshold, so an observer never fires on
       an instant jump (anchor link, End key, restored scroll position) - §10.59.
       `kmhpStickyTicking` is component-unique on purpose (§10.59 again).

       No FAB clearance to toggle here, unlike the LP's `body.kmh-sticky-on` and
       Corporate's `body.ktc-sticky-on`: the sitewide WhatsApp FAB is not
       rendered on a `magic-hour` template at all, and the Magic Cart pill that
       replaces it already sits at `bottom: 96px` at every width. See the CSS. */
    var stickyBar = root.querySelector('[data-sticky]');
    var finalBand = root.querySelector('.kmhp__final');
    if (stickyBar && buyCard) {
      var kmhpStickyTicking = false;

      var syncSticky = function () {
        var cardGone = buyCard.getBoundingClientRect().bottom < 0;
        /* Hide again over the closing CTA - it carries its own order button,
           and two order buttons on screen at once is a worse offer, not a
           stronger one. */
        var atEnd = finalBand ? finalBand.getBoundingClientRect().top < window.innerHeight : false;
        stickyBar.hidden = !(cardGone && !atEnd);
      };

      window.addEventListener('scroll', function () {
        if (kmhpStickyTicking) return;
        kmhpStickyTicking = true;
        window.requestAnimationFrame(function () {
          syncSticky();
          kmhpStickyTicking = false;
        });
      }, { passive: true });
      window.addEventListener('resize', syncSticky);
      syncSticky();
    }

    /* ══════════════════════════════════════════════════════════════════════
       8. Behaviours for the REUSED retail-PDP markup.

       Ported value-for-value from kitsters-product.js rather than loading it:
       that file is 58KB of behaviour this page has no markup for (variant
       picker, bundle builder, countdown, pincode, sticky ATC), and §10.69 is
       the standing warning about running a foreign script's selectors over a
       page it was not written for - one `[data-count]` selector there replaced
       an entire cart drawer with the number 0. Every query below is scoped to
       `root`, so nothing here can reach the header, the drawer or any app.
       ════════════════════════════════════════════════════════════════════ */

    /* Accordions - identical contract to kitsters-product.js: the body is the
       trigger's next sibling and visibility is the `hidden` property. */
    root.querySelectorAll('.kp-detail-acc__trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var body = trigger.nextElementSibling;
        if (!body) return;
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!expanded));
        body.hidden = expanded;
      });
    });

    /* "Pairs well with" rail. The scrollable element is the WRAP
       (overflow-x: auto), not the inner track (width: max-content, no
       overflow) - scrollBy on the track silently no-ops. */
    root.querySelectorAll('[data-kp-pairs-wrap]').forEach(function (wrap) {
      var container = wrap.closest('.kp-fbt');
      if (!container) return;
      var track = wrap.querySelector('[data-kp-pairs-track]');
      var prevBtn = container.querySelector('[data-kp-pairs-prev]');
      var nextBtn = container.querySelector('[data-kp-pairs-next]');

      function updateArrows() {
        var atStart = wrap.scrollLeft <= 4;
        var atEnd = Math.round(wrap.scrollLeft + wrap.clientWidth) >= Math.round(wrap.scrollWidth) - 4;
        if (prevBtn) prevBtn.disabled = atStart;
        if (nextBtn) nextBtn.disabled = atEnd;
      }

      function scrollByCard(dir) {
        var firstItem = track ? track.querySelector('.kp-pairs-item') : null;
        var gap = track ? (parseInt(window.getComputedStyle(track).columnGap, 10) || 12) : 12;
        var amount = firstItem ? firstItem.offsetWidth + gap : 180;
        wrap.scrollBy({ left: dir * amount, behavior: 'smooth' });
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { scrollByCard(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { scrollByCard(1); });

      requestAnimationFrame(updateArrows);
      var kmhpPairsTicking = false;
      wrap.addEventListener('scroll', function () {
        if (kmhpPairsTicking) return;
        kmhpPairsTicking = true;
        requestAnimationFrame(function () { updateArrows(); kmhpPairsTicking = false; });
      }, { passive: true });
      window.addEventListener('load', updateArrows);
    });

    /* ── Column SYNCHRONISER - geometry probe only; the motion is CSS ──────
       Owner 2026-08-17: "Left and right side column scrolling should be
       synchronised just like product page."

       This is a FAITHFUL PORT of the probe in kitsters-product.js - same class
       names, same custom properties, same PIN_TOP, same breakpoint - because
       the CSS half of the contract (@keyframes kp-col-sync, .kp-col-sync,
       .kp-col-unpin and the sticky base) already ships in kitsters-product.css,
       which this page loads. So "just like the product page" is true by
       construction rather than by resemblance: both pages drive the same rules
       with the same numbers, and retuning one moves both.

       Until now this page had the CSS but not the probe, so it sat in the
       documented `position: sticky` FALLBACK: the gallery pinned at the top and
       the (much taller) right column scrolled on past it, leaving the left half
       blank for thousands of pixels. That is precisely the state §10.56 says
       the synchroniser exists to fix.

       The motion is a CSS scroll-progress timeline, so the browser runs it on
       the compositor. This code never animates and never listens to scroll - a
       main-thread transform lands one frame behind a composited scroll BY
       DEFINITION, which is the "still jerky" verdict §10.56 already paid for.
       It only publishes, on RESIZE ONLY, the three numbers CSS cannot compute:

           --kp-travel      D = containerH - columnH, the distance owed
           --kp-sync-start  document offset where the row meets the pin line
           --kp-sync-end    ...and where its bottom reaches the viewport bottom

       Degradation: no `scroll()` support (Firefox today) or reduced-motion ->
       the class is never added and the CSS sticky pin takes over. Smooth, but
       the columns do not end together. Stated plainly rather than papered over
       with a JS path that would be jerky. */
    (function () {
      var container = root.querySelector('.kp-above-fold');
      var leftCol = container ? container.querySelector('.kp-left-col') : null;
      if (!container || !leftCol) return;

      var CSS_SYNC = window.CSS && CSS.supports &&
                     CSS.supports('animation-timeline', 'scroll()');

      /* Must stay identical to the pin's CSS breakpoint in
         kitsters-product.css - the width where the shell becomes two-column.
         A behaviour's breakpoint has to come from the layout rule that creates
         the context it needs, never from a number that looked right (§10.56). */
      var twoCol = window.matchMedia('(min-width: 768px)');
      var calm = window.matchMedia('(prefers-reduced-motion: reduce)');

      var PIN_TOP = 96;   // clears the sticky header; matches the CSS pin

      /* DRIFT_MAX - the column may run at most this many px AHEAD of the page over
         the whole pinned phase. The synchroniser's drift is arithmetic, not a
         choice: it is exactly (viewportH - PIN_TOP - columnH), the empty space
         under the column when it is pinned. Measured 2026-08-18 at 1440px:

             retail PDP,  900px window, column 740px ->  64px drift (looks pinned)
             retail PDP, 1080px window, column 740px -> 244px drift
             Magic Hour,  900px window, column 537px -> 267px drift

         Uncapped, a short column therefore CREEPS DOWN the screen and opens a void
         between the header and the gallery - owner-reported on the Magic Hour PDP
         ("the 2 column synchronise scrolling is not great... use the same
         synchronise scrolling" as the retail PDP). The retail PDP only ever looked
         right because its column happens to be tall enough for the drift to
         vanish, and it has the same defect on a 1080px-tall monitor.

         64px is the retail PDP's own natural drift at 1440x900 - the geometry the
         effect was tuned and signed off on - so this cap is a NO-OP there and
         makes every other column/viewport combination behave like it. Whatever
         travel is given up stays as slack BELOW the gallery, which is what the
         pinned retail PDP already shows, rather than as a gap above it.
         Not capped when the drift is negative (column taller than the visible
         band): there the column must run slower than the page or its own bottom
         never becomes reachable. */
      var DRIFT_MAX = 64;

      var kmhpSyncQueued = false;

      function syncOff(unpin) {
        leftCol.classList.remove('kp-col-sync');
        /* The desktop row is `flex-wrap: wrap` and the two columns do not
           actually fit until ~1150px, so 1024-~1150px renders STACKED. Pinning
           a stacked gallery makes the buy column scroll underneath it, which is
           simply wrong - hence the explicit unpin. */
        leftCol.classList.toggle('kp-col-unpin', !!unpin);
      }

      function syncMeasure() {
        kmhpSyncQueued = false;
        if (!CSS_SYNC || !twoCol.matches || calm.matches) return syncOff(false);

        /* Columns wrapped/stacked? offsetTop is a LAYOUT value and a transform
           does not affect layout, so this stays valid while animating. */
        var sibling = leftCol.nextElementSibling || leftCol.previousElementSibling;
        if (sibling && Math.abs(leftCol.offsetTop - sibling.offsetTop) > 4) {
          return syncOff(true);
        }

        var colH = leftCol.offsetHeight;
        var travel = container.offsetHeight - colH;
        var range = container.offsetHeight - (window.innerHeight - PIN_TOP);
        if (travel <= 0 || range <= 0) return syncOff(false);

        /* DRIFT CAP - identical to kitsters-product.js. Without it this page's
           537px-tall gallery slides 267px down the screen over the hero. */
        if (travel > range + DRIFT_MAX) travel = range + DRIFT_MAX;

        var top = container.getBoundingClientRect().top + window.pageYOffset;
        var startPx = Math.max(0, Math.round(top - PIN_TOP));

        leftCol.style.setProperty('--kp-travel', travel + 'px');
        leftCol.style.setProperty('--kp-sync-start', startPx + 'px');
        leftCol.style.setProperty('--kp-sync-end', (startPx + Math.round(range)) + 'px');
        leftCol.classList.add('kp-col-sync');
        leftCol.classList.remove('kp-col-unpin');
      }

      function syncRequest() {
        if (kmhpSyncQueued) return;
        kmhpSyncQueued = true;
        window.requestAnimationFrame(syncMeasure);
      }

      window.addEventListener('resize', syncRequest, { passive: true });
      window.addEventListener('load', syncRequest);
      if (twoCol.addEventListener) twoCol.addEventListener('change', syncRequest);
      if (calm.addEventListener) calm.addEventListener('change', syncRequest);

      if (window.ResizeObserver) {
        /* The accordions, the order-details panel and late-loading images all
           change the heights D depends on. Deferred a frame so the observer
           never writes inside its own callback and trip a resize loop. */
        var ro = new ResizeObserver(syncRequest);
        ro.observe(leftCol);
        ro.observe(container);
      }

      syncMeasure();
    })();

    /* Review row scroller. */
    (function () {
      var btn = root.querySelector('#kp-rr-scroll');
      var row = root.querySelector('#kp-rr-cards');
      if (!btn || !row) return;
      btn.addEventListener('click', function () {
        var card = row.querySelector('.kp-rr__card');
        var step = card ? card.offsetWidth + 12 : 260;
        row.scrollBy({ left: step, behavior: 'smooth' });
      });
      row.addEventListener('scroll', function () {
        var atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 8;
        btn.style.display = atEnd ? 'none' : '';
      }, { passive: true });
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
